// src/hooks/user/useUnfollowUser.js
import { useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { unfollowUser } from "../../utils/follow/followApi";
import useToast from "../useToast";
import {
  shouldRetryError,
  calculateRetryDelay,
} from "../../utils/toast/toastHelpers";
import {
  handleFollowError,
  createFollowRetryFn,
} from "../../utils/follow/followErrorHandler";
import { followQueryKeys } from "../../utils/follow/queryConfig";

/**
 * Custom hook for unfollowing a user with optimistic updates
 *
 * Implements optimistic UI updates, error handling with rollback,
 * and toast notifications for user feedback.
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 8.1, 8.2
 *
 * @returns {Object} Mutation object with unfollowUser function
 */
export const useUnfollowUser = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    async (userId) => {
      const token = await getAccessTokenSilently();
      return unfollowUser(userId, token);
    },
    {
      // Optimistic update with enhanced cache management
      onMutate: async (userId) => {
        const startTime = Date.now();

        // Cancel any outgoing refetches using correct query key
        await queryClient.cancelQueries(followQueryKeys.followStatus(userId));

        // Snapshot the previous value
        const previousFollowStatus = queryClient.getQueryData(
          followQueryKeys.followStatus(userId)
        );

        // Optimistically update follow status with nested structure
        queryClient.setQueryData(
          followQueryKeys.followStatus(userId),
          (old) => ({
            ...old,
            data: {
              ...old?.data,
              isFollowing: false,
              followerCount: Math.max((old?.data?.followerCount || 1) - 1, 0),
            },
          })
        );

        // Update current user's following count
        queryClient.setQueryData("fetchUser", (old) => ({
          ...old,
          followingCount: Math.max((old?.followingCount || 1) - 1, 0),
        }));

        // Update current user's following list pagination totals
        if (user?.sub) {
          queryClient.setQueriesData(
            {
              predicate: (query) => {
                const [base, type, listType, targetUserId] = query.queryKey;
                return (
                  base === "follow" &&
                  type === "lists" &&
                  listType === "following" &&
                  targetUserId === user.sub
                );
              },
            },
            (old) => {
              if (old?.pagination) {
                return {
                  ...old,
                  pagination: {
                    ...old.pagination,
                    total: Math.max((old.pagination.total || 1) - 1, 0),
                  },
                };
              }
              return old;
            }
          );
        }

        // Update target user's profile data (decrease follower count)
        queryClient.setQueryData(["user", userId], (old) => ({
          ...old,
          followerCount: Math.max((old?.followerCount || 1) - 1, 0),
        }));

        // Update follower list pagination totals for this user
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [base, type, listType, targetUserId] = query.queryKey;
              return (
                base === "follow" &&
                type === "lists" &&
                listType === "followers" &&
                targetUserId === userId
              );
            },
          },
          (old) => {
            if (old?.pagination) {
              return {
                ...old,
                pagination: {
                  ...old.pagination,
                  total: Math.max((old.pagination.total || 1) - 1, 0),
                },
              };
            }
            return old;
          }
        );

        // Update bulk follow status caches that include this user
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [, , type] = query.queryKey;
              return type === "bulk";
            },
          },
          (old) => {
            if (old && old[userId]) {
              return {
                ...old,
                [userId]: {
                  ...old[userId],
                  isFollowing: false,
                },
              };
            }
            return old;
          }
        );

        // Track performance
        const { trackFollowUserInteraction } = await import(
          "../../utils/follow/performanceMonitor"
        );
        trackFollowUserInteraction("unfollow", userId, startTime);

        // Return context for rollback
        return { previousFollowStatus, userId };
      },
      onSuccess: (data, userId) => {
        // Invalidate and refetch follow status to get server state
        queryClient.invalidateQueries(followQueryKeys.followStatus(userId));
        queryClient.invalidateQueries("fetchUser");

        // Invalidate target user's profile to refresh their follower count
        queryClient.invalidateQueries(["user", userId]);

        // Invalidate ALL follower/following lists that might be affected
        queryClient.invalidateQueries({
          predicate: (query) => {
            const [base, type, listType] = query.queryKey;
            return (
              base === "follow" &&
              type === "lists" &&
              (listType === "followers" || listType === "following")
            );
          },
        });

        // Invalidate bulk follow status queries
        queryClient.invalidateQueries({
          predicate: (query) => {
            const [, , type, userIds] = query.queryKey;
            return (
              type === "bulk" &&
              Array.isArray(userIds) &&
              userIds.includes(userId)
            );
          },
        });

        toast.success("Successfully unfollowed user!", {
          duration: 3000,
        });
      },
      onError: (error, userId, context) => {
        // Rollback optimistic update
        if (context?.previousFollowStatus) {
          queryClient.setQueryData(
            followQueryKeys.followStatus(userId),
            context.previousFollowStatus
          );
        }

        // Rollback current user's following count
        queryClient.setQueryData("fetchUser", (old) => ({
          ...old,
          followingCount: (old?.followingCount || 0) + 1,
        }));

        // Rollback current user's following list pagination totals
        if (user?.sub) {
          queryClient.setQueriesData(
            {
              predicate: (query) => {
                const [base, type, listType, targetUserId] = query.queryKey;
                return (
                  base === "follow" &&
                  type === "lists" &&
                  listType === "following" &&
                  targetUserId === user.sub
                );
              },
            },
            (old) => {
              if (old?.pagination) {
                return {
                  ...old,
                  pagination: {
                    ...old.pagination,
                    total: (old.pagination.total || 0) + 1,
                  },
                };
              }
              return old;
            }
          );
        }

        // Rollback target user's follower count
        queryClient.setQueryData(["user", userId], (old) => ({
          ...old,
          followerCount: (old?.followerCount || 0) + 1,
        }));

        // Rollback follower list pagination totals for this user
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [base, type, listType, targetUserId] = query.queryKey;
              return (
                base === "follow" &&
                type === "lists" &&
                listType === "followers" &&
                targetUserId === userId
              );
            },
          },
          (old) => {
            if (old?.pagination) {
              return {
                ...old,
                pagination: {
                  ...old.pagination,
                  total: (old.pagination.total || 0) + 1,
                },
              };
            }
            return old;
          }
        );

        // Rollback bulk follow status caches
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [, , type] = query.queryKey;
              return type === "bulk";
            },
          },
          (old) => {
            if (old && old[userId]) {
              return {
                ...old,
                [userId]: {
                  ...old[userId],
                  isFollowing: true,
                },
              };
            }
            return old;
          }
        );

        // Create retry function
        const retryFn = createFollowRetryFn(
          async (uid) => {
            const token = await getAccessTokenSilently();
            return unfollowUser(uid, token);
          },
          [userId],
          toast
        );

        // Handle error with centralized error handler
        handleFollowError(error, "unfollow", toast, retryFn);
      },
      retry: (failureCount, error) => {
        return shouldRetryError(error, failureCount, 2);
      },
      retryDelay: (attemptIndex) =>
        calculateRetryDelay(attemptIndex, 1000, 3000),
    }
  );
};
